import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const LayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing['2xl']};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
    background-size: 26px 26px;
    opacity: 0.22;
    mask-image: radial-gradient(circle at center, rgba(0, 0, 0, 0.95), transparent 70%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 14% 24%, rgba(42, 95, 168, 0.12), transparent 26%),
      radial-gradient(circle at 86% 20%, rgba(30, 42, 120, 0.1), transparent 24%),
      radial-gradient(circle at 72% 78%, rgba(240, 38, 33, 0.06), transparent 24%);
    filter: blur(28px);
    opacity: 0.5;
    pointer-events: none;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing.lg};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md};
  }
`;

const Shell = styled.div`
  width: min(1160px, 100%);
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: ${theme.spacing['2xl']};
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const InfoPanel = styled.div`
  background: linear-gradient(145deg, rgba(16, 23, 38, 0.97), rgba(12, 18, 30, 0.99));
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 20px 80px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.02);
  border-radius: ${theme.borderRadius['3xl']};
  padding: ${theme.spacing['2xl']};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: -40%;
    background: radial-gradient(circle at 30% 30%, rgba(42, 95, 168, 0.16), transparent 45%),
      radial-gradient(circle at 70% 60%, rgba(30, 42, 120, 0.18), transparent 50%);
    filter: blur(18px);
    opacity: 0.88;
    pointer-events: none;
  }
`;

const InfoContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const Badge = styled.span`
  align-self: flex-start;
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.full};
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.08);
  color: ${theme.colors.textSecondary};
  font-size: ${theme.fontSizes.xs};
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  font-family: ${theme.fonts.heading};
  font-weight: ${theme.fontWeights.bold};
  font-size: clamp(${theme.fontSizes['2xl']}, 3vw, ${theme.fontSizes['4xl']});
  color: ${theme.colors.textPrimary};
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const HeroSubtitle = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.fontSizes.md};
  line-height: 1.6;
  max-width: 34ch;
`;

const ModuleGrid = styled.div`
  display: grid;
  gap: ${theme.spacing.md};
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const ModuleCard = styled.div`
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.xl};
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.03));
  border: 1px solid rgba(120, 138, 170, 0.2);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const ModuleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.textPrimary};
`;

const ModuleBadge = styled.span`
  width: 28px;
  height: 28px;
  border-radius: ${theme.borderRadius.full};
  background: rgba(36, 95, 163, 0.18);
  border: 1px solid rgba(42, 95, 168, 0.4);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.fontWeights.semibold};
`;

const ModuleTitle = styled.div`
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.textPrimary};
`;

const ModuleText = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.fontSizes.sm};
  line-height: 1.5;
  margin: 0;
`;

const Card = styled.div`
  background: linear-gradient(170deg, rgba(17, 24, 40, 0.97), rgba(12, 18, 29, 0.99));
  border-radius: ${theme.borderRadius['3xl']};
  box-shadow: 0 20px 80px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.03);
  padding: ${theme.spacing['2xl']};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  width: 100%;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(16px);
  border: 1px solid rgba(122, 138, 168, 0.16);

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.xl};
  }
  
  @media (min-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing['3xl']};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  margin-bottom: 0;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const LogoIcon = styled.div`
  width: 52px;
  height: 52px;
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.16), rgba(255, 255, 255, 0.04));
  border-radius: ${theme.borderRadius['2xl']};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.textPrimary};
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.bold};
  box-shadow: 0 10px 30px rgba(14, 22, 42, 0.34);
  border: 1px solid rgba(255, 255, 255, 0.16);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 5px;
    border-radius: 14px;
    background:
      linear-gradient(135deg, transparent 47%, rgba(255, 255, 255, 0.92) 48%, rgba(255, 255, 255, 0.92) 52%, transparent 53%),
      linear-gradient(45deg, transparent 47%, rgba(255, 255, 255, 0.92) 48%, rgba(255, 255, 255, 0.92) 52%, transparent 53%),
      linear-gradient(135deg, ${theme.colors.primary} 0 50%, transparent 50%),
      linear-gradient(225deg, ${theme.colors.secondary} 0 50%, transparent 50%),
      linear-gradient(315deg, ${theme.colors.accent} 0 50%, transparent 50%),
      linear-gradient(45deg, ${theme.colors.primary} 0 50%, transparent 50%);
    background-repeat: no-repeat;
    background-size: 100% 100%;
    transform: rotate(45deg) scale(0.84);
  }
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Brand = styled.h1`
  font-size: ${theme.fontSizes['xl']};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

const BrandSub = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Pill = styled.span`
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.textPrimary};
  background: rgba(30, 42, 120, 0.2);
  border: 1px solid rgba(42, 95, 168, 0.34);
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  letter-spacing: 0.04em;
`;

const Title = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.textSecondary};
  margin: 0;
`;

interface LayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
}

export const Layout: React.FC<LayoutProps> = ({ children, title, subtitle }) => {
  return (
    <LayoutContainer>
      <Shell>
        <InfoPanel className="slide-in">
          <InfoContent>
            <Badge>XY Intelligent Information Platform</Badge>
            <HeroTitle>XYQB分析平台</HeroTitle>
            <HeroSubtitle>
              聚合多源数据、智能报告、知识问答与安全审计能力，支撑受控数据场景下的统一检索、理解、生成与导出。
            </HeroSubtitle>
            <ModuleGrid>
              <ModuleCard>
                <ModuleHeader>
                  <ModuleBadge>01</ModuleBadge>
                  <ModuleTitle>内部报告系统</ModuleTitle>
                </ModuleHeader>
                <ModuleText>对接授权数据源，支持模版选择、语言切换与多格式导出，快速形成标准化报告。</ModuleText>
              </ModuleCard>
              <ModuleCard>
                <ModuleHeader>
                  <ModuleBadge>02</ModuleBadge>
                  <ModuleTitle>数据安全智能检索</ModuleTitle>
                </ModuleHeader>
                <ModuleText>融合公开与分级数据检索，强化权限审计与访问边界控制，降低越权风险。</ModuleText>
              </ModuleCard>
              <ModuleCard>
                <ModuleHeader>
                  <ModuleBadge>03</ModuleBadge>
                  <ModuleTitle>智能翻译与报告平台</ModuleTitle>
                </ModuleHeader>
                <ModuleText>面向本地文献资料提供翻译、理解、问答与简报生成，提升跨语言资料处理效率。</ModuleText>
              </ModuleCard>
              <ModuleCard>
                <ModuleHeader>
                  <ModuleBadge>04</ModuleBadge>
                  <ModuleTitle>智能阅读与信息抽取</ModuleTitle>
                </ModuleHeader>
                <ModuleText>从聚合平台、上传资料和授权文档中提炼高价值信息，加速摘要、结构化抽取与研判。</ModuleText>
              </ModuleCard>
              <ModuleCard>
                <ModuleHeader>
                  <ModuleBadge>05</ModuleBadge>
                  <ModuleTitle>智能学习与问答助理</ModuleTitle>
                </ModuleHeader>
                <ModuleText>支持按业务主题构建专属 AI 助手，持续学习最新资料并提供实时咨询与政策解读。</ModuleText>
              </ModuleCard>
              <ModuleCard>
                <ModuleHeader>
                  <ModuleBadge>06</ModuleBadge>
                  <ModuleTitle>智能密级判定与审计</ModuleTitle>
                </ModuleHeader>
                <ModuleText>覆盖上传审计与报告导出前审查，自动标注密级并保留合规追踪链路。</ModuleText>
              </ModuleCard>
            </ModuleGrid>
          </InfoContent>
        </InfoPanel>

        <Card className="fade-in">
          <Header>
            <Logo>
              <LogoIcon>XY</LogoIcon>
              <LogoText>
                <Brand>XYQB</Brand>
                <BrandSub>分析平台</BrandSub>
              </LogoText>
            </Logo>
            <Pill>安全智能协同</Pill>
          </Header>
          <div>
            <Title>{title}</Title>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </div>
          {children}
        </Card>
      </Shell>
    </LayoutContainer>
  );
};
